import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Calendar as CalIcon, Inbox, ChevronLeft, ChevronRight, DollarSign, X, Clock, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api } from '../context/AuthContext';
import { browserTz, tzLabel, formatInTz, wallClockToUtc } from '../lib/tz';
import { grossFromNet } from '../lib/fees';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import './Notifications.css';

const MAX_NEGOTIATION_ROUNDS = 3;

// --- Small utilities ---
const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const weekdayShort = ['S','M','T','W','T','F','S'];

const toLocalKey = (isoOrDate) => {
  if (!isoOrDate) return '';
  const d = new Date(isoOrDate);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

// --- Calendar ---
const Calendar = ({ eventsByDay, selectedKey, onSelect, viewMonth, onChangeMonth }) => {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDayIdx = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = toLocalKey(new Date());

  const cells = [];
  for (let i = 0; i < firstDayIdx; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="alerts-calendar" data-testid="alerts-calendar">
      <div className="cal-header">
        <button className="cal-nav" onClick={() => onChangeMonth(-1)} aria-label="Previous month" data-testid="cal-prev">
          <ChevronLeft size={18} />
        </button>
        <h3 className="cal-title">{monthNames[month]} {year}</h3>
        <button className="cal-nav" onClick={() => onChangeMonth(1)} aria-label="Next month" data-testid="cal-next">
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="cal-weekdays">
        {weekdayShort.map((w, i) => <div key={i} className="cal-weekday">{w}</div>)}
      </div>
      <div className="cal-grid">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="cal-cell empty" />;
          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const count = eventsByDay[key]?.length || 0;
          const isToday = key === todayKey;
          const isSelected = key === selectedKey;
          return (
            <button
              key={i}
              className={`cal-cell ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${count ? 'has-events' : ''}`}
              onClick={() => onSelect(key)}
              data-testid={`cal-day-${d}`}
            >
              <span className="cal-day-num">{d}</span>
              {count > 0 && <span className="cal-dot" aria-label={`${count} events`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Rich card for an upcoming/confirmed booking — shows counterparty, scheduled
// time, duration, student's topic note, and a status pill (Opens at HH:MM /
// Waiting for X to respond / Mentor suggested a new time).
const fmtTime24 = (iso) => {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
};
const fmtDateShort = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
};

const BookingCard = ({ b, onOpen, viewerTz, viewerLocation, onAccept, onDecline, onSuggest, onCancel, acting }) => {
  const whenIso = b.suggested_time || b.scheduled_date;
  const name = b.counterparty_name || b.counterparty_username || 'Session';
  const avatar = b.counterparty_avatar;
  const isCreator = b.viewer_role === 'creator';
  const viewerRole = isCreator ? 'creator' : 'user';
  const isPending = b.status === 'pending';
  const isSuggested = b.status === 'time_suggested';
  const isConfirmed = ['confirmed', 'room_created', 'in_progress'].includes(b.status);
  const price = Number(isCreator ? (b.price || 0) : (b.gross_price || grossFromNet(b.price))).toFixed(2);
  const [historyOpen, setHistoryOpen] = useState(false);

  const otherTz = b.counterparty_timezone || '';
  const otherLoc = b.counterparty_location || '';
  const myLabel = tzLabel(viewerTz, viewerLocation);
  const otherLabel = tzLabel(otherTz, otherLoc);
  const otherHasDifferentTz = otherTz && viewerTz && otherTz !== viewerTz;

  // Negotiation state — exposed on both /bookings/user/* and /bookings/requests/*.
  const rounds = Number(b.negotiation_rounds || 0);
  const pendingFrom = b.pending_response_from
    || (b.status === 'pending' ? 'creator' : (b.status === 'time_suggested' ? 'user' : null));
  const myTurn = (isPending || isSuggested) && pendingFrom === viewerRole;
  const canCounter = myTurn && rounds < MAX_NEGOTIATION_ROUNDS;
  const canCancel = (isPending || isSuggested) && !myTurn && !!onCancel;
  const lastSuggestedBy = b.last_suggested_by;
  const history = Array.isArray(b.negotiation_history) ? b.negotiation_history : [];

  let statusText = '';
  let statusIcon = null;
  let statusColor = '#4B5563';
  if (isConfirmed) {
    statusText = whenIso ? `Opens at ${fmtTime24(whenIso)}` : 'Confirmed';
    statusIcon = <Video size={14} />;
    statusColor = '#166534';
  } else if (myTurn) {
    statusText = isSuggested
      ? `${(lastSuggestedBy === 'creator' ? name.split(' ')[0] : 'You')} suggested a new time — your move`
      : 'Awaiting your response…';
    statusColor = '#92400E';
  } else if (isPending) {
    statusText = `Waiting for ${name.split(' ')[0]} to respond…`;
    statusColor = '#92400E';
  } else if (isSuggested) {
    statusText = `${name.split(' ')[0]} is reviewing your suggested time…`;
    statusColor = '#5B21B6';
  }

  const stop = (e) => { e.stopPropagation(); };

  return (
    <div
      className="booking-card"
      data-testid={`booking-card-${b.id}`}
      style={{
        background: '#1A1A1A', border: '1px solid #262626', borderRadius: 14, padding: 14, marginBottom: 10, color: '#fff',
      }}
    >
      <button
        onClick={() => onOpen && onOpen(b)}
        disabled={!onOpen}
        style={{ display: 'block', width: '100%', textAlign: 'left', cursor: onOpen ? 'pointer' : 'default', background: 'transparent', border: 'none', padding: 0, color: 'inherit' }}
        data-testid={`booking-card-main-${b.id}`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {avatar
            ? <img src={avatar} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
            : <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#E07A5F', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{name.charAt(0)}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</p>
            <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF' }}>
              {isCreator ? 'their booking with you' : 'your booking with them'} · {b.minutes || 30} min · ${price} AUD
            </p>
          </div>
        </div>

        {whenIso && (
          <div style={{ marginBottom: b.student_note ? 10 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#D1D5DB' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CalIcon size={14} /> {formatInTz(whenIso, viewerTz, { hour12: false, year: undefined })}
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6B7280' }} data-testid={`when-your-${b.id}`}>
              Your time{myLabel ? ` · ${myLabel}` : ''}
            </p>
            {otherHasDifferentTz && (
              <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(224,122,95,0.08)', borderRadius: 8 }}>
                <p style={{ margin: 0, fontSize: 12, color: '#E5E7EB' }} data-testid={`when-other-${b.id}`}>
                  <Clock size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {formatInTz(whenIso, otherTz, { hour12: false, year: undefined })}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                  {isCreator ? 'Student' : 'Mentor'}'s time{otherLabel ? ` · ${otherLabel}` : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {b.student_note && (
          <div style={{ borderLeft: '3px solid #E07A5F', paddingLeft: 10, margin: '10px 0', color: '#E5E7EB', fontStyle: 'italic', fontSize: 14, lineHeight: 1.4 }}>
            "{b.student_note}"
          </div>
        )}

        {statusText && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: statusColor, fontSize: 13, fontWeight: 600,
                        background: 'rgba(255,255,255,0.04)', padding: '6px 12px', borderRadius: 999, marginTop: 4 }}>
            {statusIcon} {statusText}
          </div>
        )}
      </button>

      {/* Negotiation history (collapsed) */}
      {history.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            onClick={(e) => { stop(e); setHistoryOpen(v => !v); }}
            data-testid={`history-toggle-${b.id}`}
            style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', padding: 0 }}
          >
            {historyOpen ? 'Hide' : 'Show'} proposal history ({history.length})
          </button>
          {historyOpen && (
            <div style={{ marginTop: 8, borderTop: '1px solid #262626', paddingTop: 8 }} data-testid={`history-${b.id}`}>
              {history.map((h, i) => {
                const byMe = h.by === viewerRole;
                return (
                  <div key={i} style={{ padding: '6px 0', fontSize: 12, color: '#D1D5DB' }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>
                      {byMe ? 'You' : name.split(' ')[0]} proposed · {formatInTz(h.proposed_time, viewerTz, { hour12: false, year: undefined })}
                    </p>
                    {h.comment && (
                      <p style={{ margin: '2px 0 0', color: '#9CA3AF', fontStyle: 'italic' }}>"{h.comment}"</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Action pills — only when it's the viewer's turn */}
      {myTurn && (onAccept || onDecline || onSuggest) && (
        <div style={{ display: 'flex', gap: 6, marginTop: 12 }} data-testid={`actions-${b.id}`}>
          {onDecline && (
            <button
              onClick={(e) => { stop(e); onDecline(b); }}
              disabled={acting}
              data-testid={`action-decline-${b.id}`}
              style={{ flex: 1, padding: '10px 8px', borderRadius: 999, border: '1px solid #3F3F46', background: 'transparent', color: '#F87171', fontWeight: 700, fontSize: 13, cursor: acting ? 'wait' : 'pointer' }}
            >Decline</button>
          )}
          {onSuggest && canCounter && (
            <button
              onClick={(e) => { stop(e); onSuggest(b); }}
              disabled={acting}
              data-testid={`action-suggest-${b.id}`}
              style={{ flex: 1, padding: '10px 8px', borderRadius: 999, border: '1px solid #5B21B6', background: 'transparent', color: '#C4B5FD', fontWeight: 700, fontSize: 13, cursor: acting ? 'wait' : 'pointer' }}
            >Suggest time</button>
          )}
          {onAccept && (
            <button
              onClick={(e) => { stop(e); onAccept(b); }}
              disabled={acting}
              data-testid={`action-accept-${b.id}`}
              style={{ flex: 1.2, padding: '10px 8px', borderRadius: 999, border: 'none', background: '#166534', color: '#fff', fontWeight: 700, fontSize: 13, cursor: acting ? 'wait' : 'pointer' }}
            >Accept</button>
          )}
        </div>
      )}
      {myTurn && !canCounter && rounds >= MAX_NEGOTIATION_ROUNDS && (
        <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }} data-testid={`final-notice-${b.id}`}>
          This is the final proposal — accept or decline only.
        </p>
      )}

      {/* Waiting-side cancel button */}
      {canCancel && (
        <div style={{ marginTop: 10 }}>
          <button
            onClick={(e) => { stop(e); onCancel(b); }}
            disabled={acting}
            data-testid={`action-cancel-${b.id}`}
            style={{ width: '100%', padding: '8px', borderRadius: 999, border: '1px solid #3F3F46', background: 'transparent', color: '#9CA3AF', fontWeight: 600, fontSize: 12, cursor: acting ? 'wait' : 'pointer' }}
          >Cancel request</button>
        </div>
      )}
    </div>
  );
};

const RequestCard = ({ req, onAccept, onDecline, onSuggest, acting, viewerTz, viewerLocation }) => {
  const whenIso = req.scheduled_date;
  const otherTz = req.requester_timezone || '';
  const otherLoc = req.requester_location || '';
  const myLabel = tzLabel(viewerTz, viewerLocation);
  const otherLabel = tzLabel(otherTz, otherLoc);
  const otherHasDifferentTz = otherTz && viewerTz && otherTz !== viewerTz;
  return (
  <div className="alerts-req-card" data-testid={`req-card-${req.id}`}>
    <div className="req-top">
      {req.requester_avatar
        ? <img src={req.requester_avatar} alt="" className="req-avatar" />
        : <div className="req-avatar-placeholder">{(req.requester_name || 'U')[0]}</div>}
      <div className="req-meta">
        <p className="req-name">{req.requester_name || req.requester_username || 'Someone'}</p>
        <p className="req-time">Requested {formatTime(req.created_at)}</p>
      </div>
      <div className="req-price">
        <DollarSign size={14} />
        <span>{Number(req.price || 0).toFixed(0)}</span>
      </div>
    </div>
    <div className="req-body">
      <p className="req-line"><span className="req-label">Session:</span> {req.minutes} min</p>
      {whenIso && (
        <>
          <p className="req-line" data-testid={`req-when-your-${req.id}`}>
            <span className="req-label">Your time{myLabel ? ` (${myLabel})` : ''}:</span> {formatInTz(whenIso, viewerTz)}
          </p>
          {otherHasDifferentTz && (
            <p className="req-line" data-testid={`req-when-other-${req.id}`} style={{ color: '#E07A5F' }}>
              <span className="req-label">Their time{otherLabel ? ` (${otherLabel})` : ''}:</span> {formatInTz(whenIso, otherTz)}
            </p>
          )}
        </>
      )}
      {req.status === 'time_suggested' && req.suggested_time && (
        <p className="req-line" style={{ color: '#5B21B6' }}>
          <span className="req-label">You suggested:</span> {formatInTz(req.suggested_time, viewerTz)}
        </p>
      )}
      {req.selected_skills?.length > 0 && (
        <div className="req-skills">
          {req.selected_skills.slice(0, 4).map((s, i) => <span key={i} className="req-chip">{s}</span>)}
        </div>
      )}
    </div>
    <div className="req-actions">
      <button
        className="req-btn req-decline"
        onClick={() => onDecline(req.id)}
        disabled={acting}
        data-testid={`req-decline-${req.id}`}
      >
        Decline
      </button>
      <button
        className="req-btn req-suggest"
        onClick={() => onSuggest(req)}
        disabled={acting}
        data-testid={`req-suggest-${req.id}`}
      >
        Suggest time
      </button>
      <button
        className="req-btn req-accept"
        onClick={() => onAccept(req.id)}
        disabled={acting}
        data-testid={`req-accept-${req.id}`}
      >
        Accept
      </button>
    </div>
  </div>
  );
};

const NotificationItem = ({ n, onRead }) => {
  const icon = (
    n.type === 'booking' ? <CalIcon size={18} /> :
    n.type === 'booking_confirmed' ? <Check size={18} /> :
    n.type === 'booking_declined' ? <X size={18} /> :
    <Bell size={18} />
  );
  return (
    <button
      className={`alerts-notif ${!n.read ? 'unread' : ''}`}
      onClick={() => !n.read && onRead(n.id)}
      data-testid={`notif-${n.id}`}
    >
      <div className="notif-icon">{icon}</div>
      <div className="notif-body">
        <p className="notif-title">{n.title}</p>
        <p className="notif-msg">{n.message}</p>
        <span className="notif-time">{formatTime(n.created_at)}</span>
      </div>
      {!n.read && <span className="notif-unread-dot" />}
    </button>
  );
};

// Pinned "Joining in HH:MM" card for upcoming sessions within the 2-min lobby window
const _pushedBookings = new Set(); // module-level, survives remounts within the session
const PinnedSessionCard = ({ booking, navigate, isCreator }) => {
  const [secs, setSecs] = useState(() => {
    return Math.floor((new Date(booking.scheduled_date) - new Date()) / 1000);
  });
  const pushedRef = useRef(_pushedBookings.has(booking.id));

  useEffect(() => {
    const iv = setInterval(() => {
      setSecs(Math.floor((new Date(booking.scheduled_date) - new Date()) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [booking.scheduled_date]);

  useEffect(() => {
    // Teacher gets pushed at 2 min before; student gets pushed at 0
    const trigger = isCreator ? 120 : 0;
    if (!pushedRef.current && secs <= trigger) {
      pushedRef.current = true;
      _pushedBookings.add(booking.id);
      navigate(`/lobby/${booking.id}`);
    }
  }, [secs, isCreator, booking.id, navigate]);

  const mm = Math.max(0, Math.floor(secs / 60));
  const ss = Math.max(0, secs % 60);
  const label = isCreator
    ? (secs <= 120 ? 'Opening your lobby…' : `Lobby opens in ${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`)
    : (secs <= 0 ? 'Joining now…' : `Joining in ${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`);

  return (
    <div className="alerts-pinned" data-testid={`pinned-session-${booking.id}`}>
      <div className="alerts-pinned-glow" />
      <div className="alerts-pinned-inner">
        <div className="alerts-pinned-icon"><CalIcon size={22} /></div>
        <div className="alerts-pinned-text">
          <p className="alerts-pinned-title">{label}</p>
          <p className="alerts-pinned-sub">
            {isCreator ? 'You’ll enter the lobby to set up camera & audio.' :
             'Stay on this page — you’ll be pushed into the call automatically.'}
          </p>
        </div>
        <div className="alerts-pinned-time">
          <span className="pinned-mm">{String(mm).padStart(2,'0')}</span>
          <span className="pinned-sep">:</span>
          <span className="pinned-ss">{String(ss).padStart(2,'0')}</span>
        </div>
      </div>
    </div>
  );
};

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedKey, setSelectedKey] = useState(() => toLocalKey(new Date()));
  const [mobileTab, setMobileTab] = useState('calendar'); // 'calendar' | 'requests' | 'notifs'

  const viewerTz = user?.timezone || browserTz();
  const viewerLocation = user?.location || '';

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [nData, rData, bData] = await Promise.all([
        api(`/api/notifications/${user.id}`).catch(() => ({ notifications: [] })),
        api(`/api/bookings/requests/${user.id}`).catch(() => ({ requests: [] })),
        api(`/api/bookings/user/${user.id}?booking_type=upcoming`).catch(() => ({ bookings: [] })),
      ]);
      setNotifications(nData.notifications || []);
      setRequests(rData.requests || []);
      // Merge confirmed bookings into calendar via a synthetic list
      const confirmed = bData.bookings || [];
      // Store confirmed events on the requests state as well via a separate state
      setConfirmed(confirmed);
    } catch (e) {
      // ignore
    }
    setLoading(false);
  }, [user]);

  const [confirmed, setConfirmed] = useState([]);
  const [suggestTarget, setSuggestTarget] = useState(null); // { req, isoDateTime, note }

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Returning from Stripe checkout: confirm the payment, then refresh the calendar.
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId || !user) return;
    window.history.replaceState({}, '', '/notifications');
    api(`/api/payments/status/${sessionId}`)
      .then((r) => {
        if (r?.authorized || r?.payment_intent_status === 'succeeded') {
          toast.success('Booking requested', { description: 'Your mentor will confirm the time shortly.' });
        } else {
          toast.error('Payment not completed', { description: 'No booking was made. Please try again.' });
        }
        fetchAll();
      })
      .catch(() => toast.error('Payment not completed', { description: 'We couldn\u2019t confirm that payment. No booking was made.' }));
  }, [user, fetchAll]);

  const markRead = async (id) => {
    try {
      await api(`/api/notifications/${id}/read`, { method: 'POST' });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch { /* ignore */ }
  };

  const acceptReq = async (bookingOrId) => {
    const id = typeof bookingOrId === 'string' ? bookingOrId : bookingOrId.id;
    setActing(true);
    try {
      await api(`/api/bookings/${id}/accept`, { method: 'POST' });
      setRequests(prev => prev.filter(r => r.id !== id));
      toast.success('Session accepted', { description: 'The other party has been notified. See you on the call!' });
      fetchAll();
    } catch (e) { toast.error('Failed to accept', { description: e.message || 'Please try again.' }); }
    setActing(false);
  };

  const declineReq = async (bookingOrId) => {
    const id = typeof bookingOrId === 'string' ? bookingOrId : bookingOrId.id;
    setActing(true);
    try {
      await api(`/api/bookings/${id}/decline`, { method: 'POST' });
      setRequests(prev => prev.filter(r => r.id !== id));
      toast('Request declined', { description: 'The other party has been notified and any payment will be refunded.' });
      fetchAll();
    } catch (e) { toast.error('Failed to decline', { description: e.message || 'Please try again.' }); }
    setActing(false);
  };

  const cancelReq = async (booking) => {
    if (!window.confirm('Cancel this booking? Your card hold will be released and the other party notified.')) return;
    setActing(true);
    try {
      // Both parties use the same cancel semantics: student → /cancel, teacher → /decline.
      const path = booking.viewer_role === 'creator' ? 'decline' : 'cancel';
      await api(`/api/bookings/${booking.id}/${path}`, { method: 'POST' });
      toast('Booking cancelled');
      fetchAll();
    } catch (e) { toast.error('Could not cancel', { description: e.message || 'Please try again.' }); }
    setActing(false);
  };

  const suggestTime = (booking) => {
    // Open the suggest dialog pre-filled with the current proposed time
    // interpreted in the mentor's zone (so the picker UX exactly mirrors the
    // initial booking request — one shared mental model throughout the flow).
    const whenIso = booking.suggested_time || booking.scheduled_date;
    const otherTz = booking.counterparty_timezone || '';
    const myTz = user?.timezone || browserTz();
    // The picker is always in MENTOR's timezone (the "reference clock" for
    // all proposals in this booking) — if viewer is the mentor, that's `myTz`;
    // if viewer is the student, that's the counterparty's tz.
    const mentorTz = booking.viewer_role === 'creator' ? myTz : otherTz;
    let date = '', time = '13:00';
    if (whenIso) {
      try {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: mentorTz || undefined, year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(new Date(whenIso)).reduce((acc, p) => {
          if (p.type !== 'literal') acc[p.type] = p.value;
          return acc;
        }, {});
        date = `${parts.year}-${parts.month}-${parts.day}`;
        time = `${parts.hour}:${parts.minute}`;
      } catch { /* fallback below */ }
    }
    if (!date) {
      const t = new Date(Date.now() + 24 * 3600 * 1000);
      date = t.toISOString().slice(0, 10);
    }
    setSuggestTarget({ booking, mentorTz, date, time, comment: '' });
  };

  const submitSuggestion = async () => {
    if (!suggestTarget) return;
    const { booking, mentorTz, date, time, comment } = suggestTarget;
    const parsed = wallClockToUtc(date, time, mentorTz);
    if (!parsed || isNaN(parsed.getTime())) return toast.error('Pick a valid date & time');
    if (parsed.getTime() < Date.now() + 60 * 60 * 1000) return toast.error('Pick a time at least 1 hour from now');
    setSuggestTarget(null);
    setActing(true);
    try {
      const r = await api(`/api/bookings/${booking.id}/suggest-time`, {
        method: 'POST',
        body: JSON.stringify({ suggested_time: parsed.toISOString(), comment: comment.trim() || null }),
      });
      const remaining = (r.max_rounds || MAX_NEGOTIATION_ROUNDS) - (r.negotiation_rounds || 0);
      toast.success('New time suggested', {
        description: remaining <= 0
          ? 'Final proposal — the other party must accept or decline.'
          : `${remaining} counter-proposal${remaining === 1 ? '' : 's'} left in this negotiation.`,
      });
      fetchAll();
    } catch (e) { toast.error('Failed to suggest time', { description: e.message }); }
    setActing(false);
  };

  // Build a map: {YYYY-MM-DD: [event, ...]}
  // Only CONFIRMED bookings land in the calendar day panel — pending /
  // time_suggested bookings live in the Requests column instead so the user
  // can approve them or suggest a new time. The calendar dot still highlights
  // days with any kind of event so users don't miss them.
  const eventsByDay = useMemo(() => {
    const map = {};
    const push = (key, ev) => { if (!key) return; (map[key] = map[key] || []).push(ev); };
    requests.forEach(r => {
      const k = toLocalKey(r.scheduled_date || r.created_at);
      push(k, { kind: 'request', ...r });
    });
    confirmed.forEach(b => {
      const k = toLocalKey(b.scheduled_date || b.created_at);
      push(k, { kind: 'confirmed', ...b });
    });
    return map;
  }, [requests, confirmed]);

  // Bookings awaiting MY response, regardless of role. These are the bookings
  // where I need to act (accept / decline / counter-propose). Both teacher
  // incoming requests and student counter-proposals land here now that the
  // negotiation is bidirectional.
  const awaitingMyResponse = useMemo(() => {
    if (!user) return [];
    return confirmed
      .filter(b => ['pending', 'time_suggested'].includes(b.status))
      .filter(b => {
        const role = b.viewer_role === 'creator' ? 'creator' : 'user';
        const pending = b.pending_response_from
          || (b.status === 'pending' ? 'creator' : (b.status === 'time_suggested' ? 'user' : null));
        return pending === role;
      })
      .sort((a, b) => new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0));
  }, [confirmed, user]);

  // Bookings I've sent/countered and am waiting on the OTHER party. Shown in
  // the Requests column with a disabled state + "Cancel request" affordance.
  const outgoingPending = useMemo(() => {
    if (!user) return [];
    return confirmed
      .filter(b => ['pending', 'time_suggested'].includes(b.status))
      .filter(b => {
        const role = b.viewer_role === 'creator' ? 'creator' : 'user';
        const pending = b.pending_response_from
          || (b.status === 'pending' ? 'creator' : (b.status === 'time_suggested' ? 'user' : null));
        return pending && pending !== role;
      })
      .sort((a, b) => new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0));
  }, [confirmed, user]);

  // Confirmed bookings on the currently-selected calendar day (used in the
  // Calendar day panel). Falls back to an empty array when nothing is picked.
  const selectedDayConfirmed = useMemo(() => {
    if (!selectedKey) return [];
    return confirmed
      .filter(b => ['confirmed', 'room_created', 'in_progress'].includes(b.status))
      .filter(b => toLocalKey(b.scheduled_date) === selectedKey)
      .sort((a, b) => new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0));
  }, [confirmed, selectedKey]);

  const changeMonth = (delta) => {
    setViewMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + delta);
      return d;
    });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Sessions that are within their live lobby/session window (from -5 min to session end).
  // Teacher will be pushed at -2 min; student at 0. Recheck every 15s.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNowTs(Date.now()), 15000);
    return () => clearInterval(iv);
  }, []);
  const imminentSessions = useMemo(() => {
    if (!user) return [];
    return confirmed.filter(b => {
      if (!b.scheduled_date) return false;
      if (!['confirmed', 'room_created', 'in_progress'].includes(b.status)) return false;
      const secs = (new Date(b.scheduled_date) - nowTs) / 1000;
      const durationSec = (b.minutes || 30) * 60;
      // Show from 5 min before scheduled → session end
      return secs <= 300 && secs > -durationSec;
    });
  }, [confirmed, user, nowTs]);

  return (
    <div className="alerts-page" data-testid="alerts-page">
      <div className="alerts-header">
        <h1 className="alerts-title">Alerts</h1>
        <p className="alerts-sub">Your calendar, session requests & activity</p>
      </div>

      {/* Mobile tab switcher */}
      <div className="alerts-tabs">
        <button
          className={`alerts-tab ${mobileTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setMobileTab('calendar')}
          data-testid="tab-calendar"
        >
          <CalIcon size={16} /> Calendar
        </button>
        <button
          className={`alerts-tab ${mobileTab === 'requests' ? 'active' : ''}`}
          onClick={() => setMobileTab('requests')}
          data-testid="tab-requests"
        >
          <Inbox size={16} /> Requests
          {awaitingMyResponse.length > 0 && <span className="tab-badge">{awaitingMyResponse.length}</span>}
        </button>
        <button
          className={`alerts-tab ${mobileTab === 'notifs' ? 'active' : ''}`}
          onClick={() => setMobileTab('notifs')}
          data-testid="tab-notifs"
        >
          <Bell size={16} /> Notifications
          {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
      </div>

      <div className="alerts-grid">
        {/* --- COLUMN 1: CALENDAR --- */}
        <section className={`alerts-col alerts-col-calendar ${mobileTab === 'calendar' ? 'mobile-active' : ''}`}>
          <Calendar
            eventsByDay={eventsByDay}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            viewMonth={viewMonth}
            onChangeMonth={changeMonth}
          />
          <div className="cal-day-panel" data-testid="cal-day-panel">
            <h4 className="cal-day-heading">{selectedKey ? formatDate(selectedKey) : 'Select a day'}</h4>
            {selectedDayConfirmed.length === 0 ? (
              <p className="cal-day-empty">No confirmed sessions on this day.</p>
            ) : (
              <div data-testid="cal-day-bookings">
                {selectedDayConfirmed.map(b => (
                  <BookingCard
                    key={b.id}
                    b={b}
                    onOpen={(bk) => navigate(`/lobby/${bk.id}`)}
                    viewerTz={viewerTz}
                    viewerLocation={viewerLocation}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* --- COLUMN 2: BOOKING REQUESTS --- */}
        <section className={`alerts-col alerts-col-requests ${mobileTab === 'requests' ? 'mobile-active' : ''}`}>
          <div className="col-heading-row">
            <h3 className="col-heading">Booking Requests</h3>
            {(awaitingMyResponse.length + outgoingPending.length) > 0 && <span className="col-count">{awaitingMyResponse.length + outgoingPending.length}</span>}
          </div>
          {loading ? (
            <div className="alerts-empty" data-testid="requests-loading">Loading requests…</div>
          ) : (awaitingMyResponse.length === 0 && outgoingPending.length === 0) ? (
            <div className="alerts-empty" data-testid="requests-empty">
              <Inbox size={36} />
              <p className="empty-title">No pending requests</p>
              <p className="empty-sub">When someone books a session with you — or you request one — it'll appear here to approve, change, or cancel.</p>
            </div>
          ) : (
            <div className="alerts-req-list">
              {/* Your move — accept / decline / counter-propose */}
              {awaitingMyResponse.length > 0 && (
                <>
                  <p style={{ fontSize: 11, fontWeight: 800, color: '#E07A5F', letterSpacing: 0.6, textTransform: 'uppercase', margin: '4px 0 8px' }}>
                    Your move
                  </p>
                  {awaitingMyResponse.map(b => (
                    <BookingCard
                      key={b.id}
                      b={b}
                      viewerTz={viewerTz}
                      viewerLocation={viewerLocation}
                      acting={acting}
                      onAccept={acceptReq}
                      onDecline={declineReq}
                      onSuggest={suggestTime}
                    />
                  ))}
                </>
              )}
              {/* Waiting on the other party */}
              {outgoingPending.length > 0 && (
                <>
                  <p style={{ fontSize: 11, fontWeight: 800, color: '#9CA3AF', letterSpacing: 0.6, textTransform: 'uppercase', margin: '16px 0 8px' }}>
                    Waiting on the other party
                  </p>
                  {outgoingPending.map(b => (
                    <BookingCard
                      key={b.id}
                      b={b}
                      viewerTz={viewerTz}
                      viewerLocation={viewerLocation}
                      acting={acting}
                      onCancel={cancelReq}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </section>

        {/* --- COLUMN 3: OTHER NOTIFICATIONS --- */}
        <section className={`alerts-col alerts-col-notifs ${mobileTab === 'notifs' ? 'mobile-active' : ''}`}>
          <div className="col-heading-row">
            <h3 className="col-heading">Notifications</h3>
            {unreadCount > 0 && <span className="col-count col-count-accent">{unreadCount}</span>}
          </div>
          {imminentSessions.map(b => (
            <PinnedSessionCard
              key={b.id}
              booking={b}
              navigate={navigate}
              isCreator={user?.id === b.creator_id}
            />
          ))}
          {loading ? (
            <div className="alerts-empty" data-testid="notifs-loading">Loading…</div>
          ) : notifications.length === 0 ? (
            <div className="alerts-empty" data-testid="notifs-empty">
              <Bell size={36} />
              <p className="empty-title">You're all caught up</p>
              <p className="empty-sub">Likes, comments, follows and booking updates will show up here.</p>
            </div>
          ) : (
            <div className="alerts-notif-list">
              {notifications.map(n => (
                <NotificationItem key={n.id} n={n} onRead={markRead} />
              ))}
            </div>
          )}
        </section>
      </div>

      <Dialog open={!!suggestTarget} onOpenChange={(o) => !o && setSuggestTarget(null)}>
        <DialogContent data-testid="suggest-dialog">
          <DialogHeader>
            <DialogTitle>Suggest a new time</DialogTitle>
            <DialogDescription>
              {(() => {
                const b = suggestTarget?.booking;
                const name = (b?.counterparty_name || '').split(' ')[0] || 'the other party';
                const rounds = Number(b?.negotiation_rounds || 0);
                const remaining = MAX_NEGOTIATION_ROUNDS - rounds;
                return (
                  <>
                    Propose an alternative slot to {name}. They'll be notified and can accept, decline or counter.
                    {remaining <= 1 && (
                      <strong style={{ display: 'block', marginTop: 6, color: '#E07A5F' }}>
                        {remaining === 1 ? 'This is your last counter-proposal — after this they can only accept or decline.' : ''}
                      </strong>
                    )}
                  </>
                );
              })()}
            </DialogDescription>
          </DialogHeader>

          {suggestTarget && (() => {
            const { booking, mentorTz, date, time, comment } = suggestTarget;
            const myTz = user?.timezone || browserTz();
            const myLocation = user?.location || '';
            const otherLocation = booking.counterparty_location || '';
            const mentorLabel = booking.viewer_role === 'creator'
              ? tzLabel(myTz, myLocation)
              : tzLabel(mentorTz, otherLocation);
            const yourLabel = tzLabel(myTz, myLocation);
            const utc = (date && time) ? wallClockToUtc(date, time, mentorTz) : null;
            const utcIso = utc ? utc.toISOString() : null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ background: '#111', borderRadius: 14, padding: 14 }}>
                  <p style={{ margin: 0, textAlign: 'center', color: '#E07A5F', fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                    MENTOR'S TIME{mentorLabel ? ` · ${mentorLabel.toUpperCase()}` : ''}
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setSuggestTarget(s => s ? { ...s, date: e.target.value } : s)}
                      data-testid="suggest-date"
                      min={new Date().toISOString().slice(0, 10)}
                      style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontSize: 15 }}
                    />
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setSuggestTarget(s => s ? { ...s, time: e.target.value } : s)}
                      data-testid="suggest-time-input"
                      style={{ width: 120, padding: '12px', borderRadius: 10, border: '2px solid #fff', background: '#1A1A1A', color: '#fff', fontSize: 15 }}
                    />
                  </div>
                  {utcIso && (
                    <p style={{ margin: '10px 0 0', textAlign: 'center', color: '#D1D5DB', fontSize: 13 }}>
                      {formatInTz(utcIso, mentorTz)}
                    </p>
                  )}
                </div>
                {utcIso && mentorTz && mentorTz !== myTz && (
                  <div style={{ background: '#0F2A24', borderRadius: 14, padding: 14, textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#4ADE80', fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                      YOUR LOCAL TIME{yourLabel ? ` · ${yourLabel.toUpperCase()}` : ''}
                    </p>
                    <p data-testid="suggest-your-local-time" style={{ margin: '6px 0 0', color: '#fff', fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 20 }}>
                      {formatInTz(utcIso, myTz)}
                    </p>
                  </div>
                )}
                {utcIso && booking.viewer_role === 'creator' && booking.counterparty_timezone && booking.counterparty_timezone !== myTz && (
                  <div style={{ background: '#0F2A24', borderRadius: 14, padding: 14, textAlign: 'center' }}>
                    <p style={{ margin: 0, color: '#4ADE80', fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                      STUDENT'S TIME{tzLabel(booking.counterparty_timezone, otherLocation) ? ` · ${tzLabel(booking.counterparty_timezone, otherLocation).toUpperCase()}` : ''}
                    </p>
                    <p data-testid="suggest-student-time" style={{ margin: '6px 0 0', color: '#fff', fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 20 }}>
                      {formatInTz(utcIso, booking.counterparty_timezone)}
                    </p>
                  </div>
                )}
                {utc && utc.getTime() < Date.now() + 60 * 60 * 1000 && (
                  <p data-testid="suggest-too-soon" style={{ margin: 0, color: '#E07A5F', fontSize: 13, textAlign: 'center' }}>
                    Times must be at least 1 hour from now.
                  </p>
                )}
                <label className="suggest-label" style={{ display: 'block', marginTop: 4 }}>
                  Comment (optional)
                  <textarea
                    rows="3"
                    maxLength={300}
                    placeholder="e.g. Can we move to Friday afternoon — morning doesn't work for me."
                    value={comment}
                    onChange={(e) => setSuggestTarget(s => s ? { ...s, comment: e.target.value.slice(0, 300) } : s)}
                    data-testid="suggest-comment"
                    className="suggest-input"
                    style={{ width: '100%', marginTop: 6, padding: 12, borderRadius: 10, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <span style={{ display: 'block', textAlign: 'right', fontSize: 11, color: '#6B7280', marginTop: 4 }}>{comment.length}/300</span>
                </label>
              </div>
            );
          })()}

          <DialogFooter>
            <button className="suggest-btn ghost" onClick={() => setSuggestTarget(null)} data-testid="suggest-cancel">
              Cancel
            </button>
            <button className="suggest-btn primary" onClick={submitSuggestion} data-testid="suggest-submit">
              Send suggestion
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
